// @ts-check
// Aba Departamentos de Configurações > Atendimento (contrato 10 de atendimento, seções 3.1.1, 4.5, 7.4, 8 e 24.4): lista,
// criar, membros com o papel Líder e "Recebe distribuição", tipo, apelido do e-mail com o endereço escola+apelido@ e o
// botão de copiar, distribuição, ativo e os avisos automáticos (recebido, transferência e fora do horário). Ouvidoria e
// Proteção mostram o aviso de que só os membros veem a fila.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { listChannels } from '../../data/conversations.js';
import { createServiceTeam, listServiceTeams, replaceServiceTeamMembers, updateServiceTeam, updateServiceTeamSettings } from '../../data/service-desk.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { isValidEmailAlias, suggestEmailAlias, teamEmailAddress } from '../../lib/service-settings-rules.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { CopyableValue } from '../settings/settings-channels-page.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Checkbox, Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { AgentStatusDot } from './service-agent-status.js';
import { TicketValueTag, serviceErrorMessage, showServiceError } from './service-display.js';
import { t } from '../../i18n/index.js';

/** Avisos automáticos do departamento (seção 7.4), na ordem da tela. */
const NOTICE_KEYS = Object.freeze(['received', 'transfer', 'outOfHours']);

/** @param {{ team: any, canEdit: boolean, inboundAddress: string | null, onChanged: () => void }} props */
function TeamCard({ team, canEdit, inboundAddress, onChanged }) {
  const [isOpen, setIsOpen] = useState(false);
  const [members, setMembers] = useState(/** @type {any[]} */ (team.members ?? []));
  const [notices, setNotices] = useState(team.notices ?? {});
  const [distribution, setDistribution] = useState(team.distribution ?? 'ROUND_ROBIN');
  const [alias, setAlias] = useState(team.emailAlias ?? '');
  const [newMemberId, setNewMemberId] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const isRestricted = team.teamKind !== 'STANDARD';
  const address = teamEmailAddress(inboundAddress, team.emailAlias);
  const availableMembers = (workspaceMembersState.value ?? []).filter((member) => !members.some((existing) => existing.workspaceMemberId === member.id));

  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (alias !== (team.emailAlias ?? '') && team.teamKind !== 'PROTECTION') await updateServiceTeam(team.id, { emailAlias: alias || null });
      await replaceServiceTeamMembers(team.id, members.map((member) => ({ workspaceMemberId: member.workspaceMemberId, memberRole: member.memberRole, receivesDistribution: member.receivesDistribution })));
      await updateServiceTeamSettings(team.id, { distribution, notices });
      showSuccessSnackbar(t('serviceSettings.saved'));
      onChanged();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  const toggleActive = async () => {
    try {
      await updateServiceTeam(team.id, { isActive: !team.isActive });
      showSuccessSnackbar(t(team.isActive ? 'serviceSettings.teams.deactivated' : 'serviceSettings.teams.activated'));
      onChanged();
    } catch (requestError) {
      showServiceError(requestError);
    }
  };
  return html`<div class="service-settings-card" data-inactive=${team.isActive ? undefined : 'true'} data-testid="service-team-card">
    <div class="service-team-header">
      <button type="button" class="service-team-toggle" aria-expanded=${isOpen ? 'true' : 'false'} onClick=${() => setIsOpen(!isOpen)}>
        <${Icon} name=${isOpen ? 'IconChevronDown' : 'IconChevronRight'} size=${14} />
        <span class="service-settings-list-title">${team.name}</span>
      </button>
      <${TicketValueTag} field="teamKind" value=${team.teamKind} />
      ${!team.isActive ? html`<span class="service-purpose-chip">${t('serviceSettings.teams.inactive')}</span>` : null}
      <span class="service-pane-title-spacer"></span>
      <span class="input-hint">${team.members ? t('serviceSettings.teams.memberCount', { count: team.members.length }) : t('serviceSettings.teams.membersHidden')}</span>
      <span class="service-open-now" data-open=${team.isOpenNow ? 'true' : 'false'}>${team.isOpenNow ? t('serviceSettings.teams.openNow') : t('serviceSettings.teams.closedNow')}</span>
    </div>
    ${isRestricted ? html`<p class="service-dialog-note"><${Icon} name="IconLock" size=${14} />${t(`serviceSettings.teams.restrictedHints.${team.teamKind}`)}</p>` : null}
    ${address ? html`<div class="service-team-address"><span class="form-field-label">${t('serviceSettings.teams.emailAddress')}</span><${CopyableValue} value=${address} label=${t('serviceSettings.teams.copyAddress')} /></div>` : null}
    ${isOpen
      ? html`<div class="service-team-body">
          ${team.teamKind !== 'PROTECTION'
            ? html`<label class="form-field"><span class="form-field-label">${t('serviceSettings.teams.emailAlias')}</span>
                <input class="text-input" value=${alias} maxLength=${30} disabled=${!canEdit} data-error=${alias && !isValidEmailAlias(alias) ? 'true' : undefined}
                  onInput=${(/** @type {Event} */ event) => setAlias(/** @type {HTMLInputElement} */ (event.currentTarget).value.toLowerCase())} />
                <span class="input-hint">${t('serviceSettings.teams.emailAliasHint')}</span></label>`
            : null}
          <h4 class="service-panel-subtitle">${t('serviceSettings.teams.members')}</h4>
          ${team.members === null
            ? html`<p class="input-hint">${t('serviceSettings.teams.membersHidden')}</p>`
            : html`<table class="service-settings-table">
                <thead><tr><th>${t('serviceSettings.teams.member')}</th><th>${t('serviceSettings.teams.lead')}</th><th>${t('serviceSettings.teams.receivesDistribution')}</th><th></th></tr></thead>
                <tbody>
                  ${members.map((member, index) => html`<tr key=${member.workspaceMemberId}>
                    <td><span class="service-member-name"><${AgentStatusDot} status=${member.status ?? 'OFFLINE'} />${member.name}</span></td>
                    <td><${Checkbox} checked=${member.memberRole === 'LEAD'} disabled=${!canEdit} ariaLabel=${t('serviceSettings.teams.lead')}
                      onChange=${(checked) => setMembers(members.map((candidate, position) => (position === index ? { ...candidate, memberRole: checked ? 'LEAD' : 'AGENT' } : candidate)))} /></td>
                    <td><${Checkbox} checked=${member.receivesDistribution} disabled=${!canEdit} ariaLabel=${t('serviceSettings.teams.receivesDistribution')}
                      onChange=${(checked) => setMembers(members.map((candidate, position) => (position === index ? { ...candidate, receivesDistribution: checked } : candidate)))} /></td>
                    <td>${canEdit ? html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('serviceSettings.teams.removeMember', { name: member.name })}
                      onClick=${() => setMembers(members.filter((_, position) => position !== index))} />` : null}</td>
                  </tr>`)}
                </tbody>
              </table>
              ${canEdit
                ? html`<div class="service-inline-form">
                    <select class="text-input" value=${newMemberId} aria-label=${t('serviceSettings.teams.addMember')}
                      onFocus=${() => loadWorkspaceMembers()} onChange=${(/** @type {Event} */ event) => setNewMemberId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                      <option value="">${t('serviceSettings.teams.chooseMember')}</option>
                      ${availableMembers.map((member) => html`<option key=${member.id} value=${member.id}>${formatFullName(member.name)}</option>`)}
                    </select>
                    <${Button} title=${t('serviceSettings.teams.addMember')} icon="IconPlus" size="small" variant="secondary" disabled=${!newMemberId} onClick=${() => {
                      const member = (workspaceMembersState.value ?? []).find((candidate) => candidate.id === newMemberId);
                      if (!member) return;
                      setMembers([...members, { workspaceMemberId: member.id, name: formatFullName(member.name), memberRole: 'AGENT', receivesDistribution: true, status: 'OFFLINE' }]);
                      setNewMemberId('');
                    }} />
                  </div>`
                : null}`}
          <label class="form-field"><span class="form-field-label">${t('serviceSettings.teams.distribution')}</span>
            <select class="text-input" value=${distribution} disabled=${!canEdit} onChange=${(/** @type {Event} */ event) => setDistribution(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
              <option value="ROUND_ROBIN">${t('serviceSettings.teams.distributions.ROUND_ROBIN')}</option>
              <option value="MANUAL">${t('serviceSettings.teams.distributions.MANUAL')}</option>
            </select>
            <span class="input-hint">${t(`serviceSettings.teams.distributionHints.${distribution}`)}</span></label>
          ${team.teamKind === 'STANDARD'
            ? html`<h4 class="service-panel-subtitle">${t('serviceSettings.teams.notices')}</h4>
              ${NOTICE_KEYS.map((key) => html`<div key=${key} class="service-notice-row">
                <div class="service-toggle-row">
                  <${Toggle} value=${Boolean(notices[key]?.enabled)} disabled=${!canEdit} ariaLabel=${t(`serviceSettings.teams.noticeLabels.${key}`)}
                    onChange=${(value) => setNotices({ ...notices, [key]: { ...(notices[key] ?? {}), enabled: value } })} />
                  <span class="form-field-label">${t(`serviceSettings.teams.noticeLabels.${key}`)}</span>
                </div>
                ${notices[key]?.enabled
                  ? html`<textarea class="text-input service-textarea" rows="2" maxLength=${1024} value=${notices[key]?.text ?? ''} disabled=${!canEdit}
                      aria-label=${t(`serviceSettings.teams.noticeLabels.${key}`)}
                      onInput=${(/** @type {Event} */ event) => setNotices({ ...notices, [key]: { ...(notices[key] ?? {}), text: /** @type {HTMLTextAreaElement} */ (event.currentTarget).value } })}></textarea>`
                  : null}
              </div>`)}
              <span class="input-hint">${t('serviceSettings.teams.noticesHint')}</span>`
            : null}
          ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
          ${canEdit
            ? html`<div class="service-inline-actions">
                <${Button} title=${team.isActive ? t('serviceSettings.teams.deactivate') : t('serviceSettings.teams.activate')} size="small" variant="secondary" onClick=${toggleActive} />
                <${Button} title=${t('serviceSettings.save')} accent="blue" size="small" isLoading=${isSaving} disabled=${Boolean(alias) && !isValidEmailAlias(alias)} onClick=${save} />
              </div>`
            : null}
        </div>`
      : null}
  </div>`;
}

/** @param {{ onCreated: () => void }} props */
function CreateTeamForm({ onCreated }) {
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [teamKind, setTeamKind] = useState('STANDARD');
  const [isAliasEdited, setIsAliasEdited] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const create = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await createServiceTeam({ name: name.trim(), teamKind, ...(teamKind !== 'PROTECTION' && alias ? { emailAlias: alias } : {}) });
      showSuccessSnackbar(t('serviceSettings.teams.created'));
      setName('');
      setAlias('');
      setIsAliasEdited(false);
      onCreated();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<div class="service-settings-card" data-testid="service-team-create">
    <span class="service-settings-list-title">${t('serviceSettings.teams.createTitle')}</span>
    <div class="form-field-row">
      <label class="form-field"><span class="form-field-label">${t('serviceSettings.teams.name')}</span>
        <input class="text-input" value=${name} maxLength=${80} onInput=${(/** @type {Event} */ event) => {
          const value = /** @type {HTMLInputElement} */ (event.currentTarget).value;
          setName(value);
          if (!isAliasEdited) setAlias(suggestEmailAlias(value));
        }} /></label>
      <label class="form-field"><span class="form-field-label">${t('serviceSettings.teams.kind')}</span>
        <select class="text-input" value=${teamKind} onChange=${(/** @type {Event} */ event) => setTeamKind(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
          ${['STANDARD', 'OMBUDSMAN', 'PROTECTION'].map((value) => html`<option key=${value} value=${value}>${t(`serviceDesk.values.teamKind.${value}`)}</option>`)}
        </select></label>
    </div>
    ${teamKind !== 'PROTECTION'
      ? html`<label class="form-field"><span class="form-field-label">${t('serviceSettings.teams.emailAlias')}</span>
          <input class="text-input" value=${alias} maxLength=${30} data-error=${alias && !isValidEmailAlias(alias) ? 'true' : undefined}
            onInput=${(/** @type {Event} */ event) => {
              setIsAliasEdited(true);
              setAlias(/** @type {HTMLInputElement} */ (event.currentTarget).value.toLowerCase());
            }} /><span class="input-hint">${t('serviceSettings.teams.emailAliasHint')}</span></label>`
      : null}
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    <div class="service-inline-actions"><${Button} title=${t('serviceSettings.teams.create')} icon="IconPlus" accent="blue" size="small" isLoading=${isSaving}
      disabled=${!name.trim() || (Boolean(alias) && !isValidEmailAlias(alias))} onClick=${create} /></div>
  </div>`;
}

/** @param {{ canEdit: boolean }} props */
export function ServiceSettingsTeamsTab({ canEdit }) {
  const [teams, setTeams] = useState(/** @type {any[] | null} */ (null));
  const [inboundAddress, setInboundAddress] = useState(/** @type {string | null} */ (null));
  const load = () => listServiceTeams().then(setTeams).catch(() => setTeams([]));
  useEffect(() => {
    load();
    loadWorkspaceMembers();
    listChannels().then((data) => setInboundAddress(data.inboundEmailAddress ?? null)).catch(() => {});
  }, []);
  return html`<div class="service-settings-tab-body" data-testid="service-settings-teams">
    <${SettingsSection} title=${t('serviceSettings.teams.title')} description=${t('serviceSettings.teams.description')}>
      ${teams === null
        ? html`<${Skeleton} height=${80} /><${Skeleton} height=${80} />`
        : teams.map((team) => html`<${TeamCard} key=${`${team.id}-${(team.members ?? []).length}-${team.isActive}`} team=${team} canEdit=${canEdit} inboundAddress=${inboundAddress} onChanged=${load} />`)}
      ${canEdit ? html`<${CreateTeamForm} onCreated=${load} />` : null}
    <//>
  </div>`;
}
